import { FC, useEffect, useMemo, useRef, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import Button from "@/components/Button";
import {
  useGetTestResult,
  useUpdateTestResultStatus,
} from "@/hooks/useEnrollments";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { CircularProgress, Typography } from "@mui/material";
import { TestResultStatusEnum } from "@/domain/enrollment.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { ACTION_CODE } from "@/domain/auditlog.enum";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import React from "react";
import AlertDialog from "@/components/AlertDialog";
import { EnrollmentStatus } from "@/domain/enrollment.interface";

type ValidationStatus =
  | TestResultStatusEnum.POSITIVE
  | TestResultStatusEnum.NEGATIVE
  | TestResultStatusEnum.FAILED;

const validationSchema = yup.object().shape({
  status: yup
    .string()
    .oneOf(
      [
        TestResultStatusEnum.NEGATIVE,
        TestResultStatusEnum.POSITIVE,
        TestResultStatusEnum.FAILED,
      ] as ValidationStatus[],
      "Please select a validation status",
    )
    .required("Please select a validation status"),
});

type ValidationFormData = yup.InferType<typeof validationSchema>;

type TestResultValidationProps = {
  open: boolean;
  onClose: () => void;
  enrollmentId: string;
  testResultId: string;
  onMaxReached: () => void;
  programName: string;
  participantName: string;
  enrollmentStatus: EnrollmentStatus;
};

const TestResultValidation: FC<TestResultValidationProps> = ({
  open,
  onClose,
  enrollmentId,
  testResultId,
  onMaxReached,
  programName,
  participantName,
  enrollmentStatus,
}) => {
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVertical, setIsVertical] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const createAuditLog = useCreateAuditlog();
  const auditLogViewCalledRef = React.useRef(false);

  const { testResult, isLoadingTestResult, isErrorTestResult } =
    useGetTestResult(enrollmentId, testResultId);

  const defaultValues = useMemo(() => {
    const status = testResult?.status;
    return {
      status: (status === TestResultStatusEnum.POSITIVE ||
      status === TestResultStatusEnum.NEGATIVE ||
      status === TestResultStatusEnum.FAILED
        ? status
        : undefined) as ValidationStatus | undefined,
    };
  }, [testResult]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ValidationFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const handleVideoLoad = () => {
    if (videoRef.current) {
      const { videoWidth, videoHeight } = videoRef.current;
      setIsVertical(videoHeight > videoWidth);
      setIsVideoLoaded(true);
    }
  };

  const {
    mutate: updateTestResultStatus,
    isPending: isUpdatingTestResultStatus,
  } = useUpdateTestResultStatus();

  const onSubmit = async (data: ValidationFormData) => {
    updateTestResultStatus(
      {
        enrollmentId,
        testResultId,
        status: data.status,
        userId: userInfo?._id || "",
      },
      {
        onSuccess: (responseData) => {
          onClose();

          if (responseData.rewardEngineResponse.params.maxReached) {
            onMaxReached();
          }
        },
        onError: (error) => {
          console.error("Error updating test result:", error);
        },
      },
    );
  };

  const isPendingOrCreated = useMemo(() => {
    const isTestResultPendingOrCreated =
      testResult?.status === TestResultStatusEnum.PENDING ||
      testResult?.status === TestResultStatusEnum.CREATED;

    const isEnrollmentNotCompleted =
      enrollmentStatus === EnrollmentStatus.ACTIVE;

    return isTestResultPendingOrCreated && isEnrollmentNotCompleted;
  }, [testResult?.status, enrollmentStatus]);

  const videoComponent = useMemo(
    () => (
      <Box>
        {isLoadingTestResult ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : isErrorTestResult ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: "error.main",
            }}
          >
            Error loading video
          </Box>
        ) : testResult?.proofParticipant &&
          Array.isArray(testResult.proofParticipant) &&
          testResult.proofParticipant.length > 0 &&
          testResult.proofParticipant[0]?.s3Url ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "black",
            }}
          >
            <video
              ref={videoRef}
              controls
              onLoadedMetadata={handleVideoLoad}
              style={{
                maxWidth: isVertical ? "310px" : "100%",
                maxHeight: isVertical ? "490px" : "300px",
                objectFit: "contain",
              }}
            >
              <source
                src={testResult.proofParticipant[0].s3Url}
                type="video/mp4"
              />
              Video not supported
            </video>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            Video not available
          </Box>
        )}
        {isPendingOrCreated && (
          <FormControl
            error={!!errors.status}
            component="fieldset"
            sx={{
              justifyContent: "center",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  row
                  aria-labelledby="validation-status-radio-group"
                  {...field}
                >
                  <FormControlLabel
                    value={TestResultStatusEnum.NEGATIVE}
                    control={<Radio />}
                    label="Negative"
                  />
                  <FormControlLabel
                    value={TestResultStatusEnum.POSITIVE}
                    control={<Radio />}
                    label="Positive"
                  />
                  <FormControlLabel
                    value={TestResultStatusEnum.FAILED}
                    control={<Radio />}
                    label="Failed"
                  />
                </RadioGroup>
              )}
            />
            {errors.status && (
              <FormHelperText>{errors.status.message}</FormHelperText>
            )}
          </FormControl>
        )}
      </Box>
    ),
    [
      isErrorTestResult,
      isLoadingTestResult,
      testResult,
      isVertical,
      control,
      errors.status,
    ],
  );

  const selfAssesComponent = useMemo(() => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: "column",
          backgroundColor: "#DEE7E7",
          alignItems: "flex-start",
          justifyContent: "center",
          height: "100%",
          padding: 2.5,
          width: "100%",
          maxWidth: 314,
        }}
      >
        <Typography variant="titleSmall" fontWeight={600}>
          Self asses result:
        </Typography>
        <Typography variant="bodyLarge">
          {testResult?.proofParticipant &&
          Array.isArray(testResult.proofParticipant) &&
          testResult.proofParticipant.length > 0
            ? testResult.proofParticipant[
                testResult.proofParticipant.length - 1
              ].answer
              ? "Positive"
              : "Negative"
            : "-"}
        </Typography>
      </Box>
    );
  }, [testResult]);

  useEffect(() => {
    if (userInfo && testResult && !auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ENROLLMENTS,
        option: OPTIONS.ENROLLMENT_DETAIL_TEST_VAL_RESULT_VIEW,
        actionCode: ACTION_CODE.WEB_ENR_PART_DETAIL_TEST_VAL_RESULT_VIEW,
        action: "When accessing to the validation results pop up",
        detail: `Viewed validation video ${testResult?.validationNumber} of ${programName} for ${participantName}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo, testResult]);

  useEffect(() => {
    if (
      !isLoadingTestResult &&
      testResult?.proofParticipant &&
      Array.isArray(testResult.proofParticipant) &&
      testResult.proofParticipant.length > 0 &&
      testResult.proofParticipant[0]?.s3Url
    ) {
      setIsVideoLoaded(true);
    }
  }, [isLoadingTestResult, testResult]);

  const handleConfirmSubmit = () => {
    setIsConfirmModalOpen(false);
    handleSubmit(onSubmit)();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        isPending={false}
        title="Validation video"
        theme={theme}
        size={{
          width: "100%",
          minHeight: 440,
          maxHeight: 750,
          maxWidth: 690,
        }}
        content={
          <Box sx={{ width: "100%", minHeight: 380, padding: "20px 0" }}>
            {!isVideoLoaded ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : isVertical ? (
              <Box sx={{ display: "flex", gap: 2, flex: 1 }}>
                <Box>{videoComponent}</Box>
                {selfAssesComponent}
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
                <Box sx={{ flex: 1 }}>{videoComponent}</Box>
                {selfAssesComponent}
              </Box>
            )}
          </Box>
        }
        actions={
          isPendingOrCreated ? (
            <Box
              sx={{
                display: "flex",
                gap: "10px",
              }}
            >
              <Button variantType="cancel" onClick={onClose} label="Cancel" />
              <Button
                variantType="save"
                onClick={() => setIsConfirmModalOpen(true)}
                label="Save"
                disabled={isUpdatingTestResultStatus}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: "10px",
              }}
            >
              <Button variantType="cancel" onClick={onClose} label="Close" />
            </Box>
          )
        }
      />
      {isConfirmModalOpen && (
        <AlertDialog
          open={isConfirmModalOpen}
          title="Confirm"
          description="Are you sure you want to save this entry? The test result cannot be edited after submission."
          onCancel={() => setIsConfirmModalOpen(false)}
          onConfirm={handleConfirmSubmit}
          confirmText="Confirm"
          cancelText="Cancel"
        />
      )}
    </>
  );
};

export default TestResultValidation;
